/**
 * Staggered entrance for collection items (CB-5a) — the template picker's
 * choreography as two small functions.
 *
 * The cap exists because 194 × 24ms is a 4.6-second entrance; 24 bounds the
 * tail at ~576ms while keeping the first two rows visibly sequential. The
 * disarm is the non-obvious half: filtering un-hides items, `[hidden]` is
 * display:none, and an element returning from display:none RESTARTS its CSS
 * animation — so a still-armed grid replays the whole entrance on every
 * keystroke. `armSettle` stamps `data-settled` after the entrance has played;
 * collection.css turns the animation off under it.
 */

import { prefersReducedMotion } from './feedback.js';

/**
 * Stamp `--pb-coll-idx` on each item, capped. A no-op under reduced motion —
 * collection.css also disables the animation there, but not writing the
 * variables at all keeps the inline style clean.
 * @param {Iterable<HTMLElement>} items
 * @param {{cap?: number}} [opts]
 */
export function applyStagger(items, { cap = 24 } = {}) {
  if (prefersReducedMotion()) return;
  let i = 0;
  for (const el of items) {
    el.style.setProperty('--pb-coll-idx', String(Math.min(i, cap)));
    i += 1;
  }
}

/**
 * Arm the settle: one frame to let the entrance begin, then a timer that
 * stamps `data-settled='true'` on the container. Returns a cancel that stops
 * BOTH halves, so a closed browser cannot write into a detached tree.
 * @param {HTMLElement} el
 * @param {{delay?: number}} [opts]
 * @returns {() => void} cancel
 */
export function armSettle(el, { delay = 900 } = {}) {
  let timer = 0;
  let raf = requestAnimationFrame(() => {
    raf = 0;
    timer = setTimeout(() => {
      el.dataset.settled = 'true';
    }, delay);
  });
  return () => {
    // Environments that polyfill requestAnimationFrame with setTimeout (the
    // jsdom suites) rarely polyfill cancelAnimationFrame too; in that world
    // the handle IS a timeout id, so clearTimeout is the correct cancel.
    if (raf) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      else clearTimeout(raf);
    }
    if (timer) clearTimeout(timer);
  };
}
